import React, { useEffect, useState } from "react";
import { Alert, Modal, Spinner } from "react-bootstrap";
import {
  TimeOffPayloadBackend,
  TimeOffRequestFrontend,
} from "../../../services/AdministrativeRequestServices/AdministrativeRequestService";
import { toast } from "react-toastify";
import { useAuth } from "../../../firebase/AuthProvider";
import {
  addUpdateTimeOff,
  getLeaveTypes,
} from "../../../services/userServices/UserInfoService";
import { UpdateLeaveRequestTypeDto } from "../../../interfaces/Employee/Employee";
import { getUpcomingTimeOffs } from "../../../services/EmployeeService/EmployeeService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";

// Remove the import for the old CSS file if you delete it
// import "./NewAdminRequestModal.css"; 

interface NewAdminRequestModalProps {
  show: boolean;
  handleClose: () => void;
}

const requestTypeOptions = [
  { value: "Time-Off Request", label: "Time-Off Request" },
];

const NewAdminRequestModal: React.FC<NewAdminRequestModalProps> = ({
  show,
  handleClose,
}) => {
  const { currentUser } = useAuth();
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiSuccessMessage, setApiSuccessMessage] = useState<string | null>(null);
  const [selectedRequestType, setSelectedRequestType] = useState<string>("Time-Off Request");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<UpdateLeaveRequestTypeDto[]>([]);

  const [formData, setFormData] = useState<TimeOffRequestFrontend>({
    userId: "",
    timeOffStartTime: "",
    timeOffEndTime: "",
    typeOfLeave: 0,
    isApproved: false,
    isRejected: false,
    comment: "",
    reason: "",
  });

  const [remainingLeaveCount, setRemainingLeaveCount] = useState<number | 0>(0);

  useEffect(() => {
    fetchLeaveTypes();
    fetchTimeOffs();
  }, [currentUser]);

  const fetchLeaveTypes = async () => {
    try {
      if (currentUser) {
        const authToken = await currentUser.getIdToken();
        const response = await getLeaveTypes(authToken);
        if ("error" in response) {
          console.error("Error loading leave types:", response.error);
        } else {
          setLeaveTypes(response);
        }
      }
    } catch (err) {
      console.error("Unexpected error while fetching leave types:", err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      userId: "",
      timeOffStartTime: "",
      timeOffEndTime: "",
      isApproved: false,
      typeOfLeave: 0,
      isRejected: false,
      comment: "",
      reason: "",
    });
  };

  const handleSubmitTimeOff = async () => {
    setApiError(null);
    setApiSuccessMessage(null);
    setIsSubmitting(true);

    if (!formData.timeOffStartTime || !formData.timeOffEndTime || !formData.typeOfLeave) {
      setApiError("Please fill in start date, end date, and type of leave.");
      setIsSubmitting(false);
      return;
    }

    const start = new Date(formData.timeOffStartTime);
    const end = new Date(formData.timeOffEndTime);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (start < today || end < today) {
      setApiError("Dates must be today or in the future.");
      setIsSubmitting(false);
      return;
    }
    if (start > end) {
      setApiError("Start date cannot be after end date.");
      setIsSubmitting(false);
      return;
    }

    if (currentUser) {
      const authToken = await currentUser.getIdToken();
      const startTimeISO = `${formData.timeOffStartTime}T00:00:00.000Z`;
      const endTimeISO = `${formData.timeOffEndTime}T23:59:59.999Z`;
      const currentUserId = (currentUser as any)?.uid;

      const payload: TimeOffPayloadBackend = {
        id: 0,
        userId: currentUserId,
        typeOfLeave: formData.typeOfLeave || 0,
        comment: formData.comment,
        isApproved: false,
        timeOffStartTime: startTimeISO,
        timeOffEndTime: endTimeISO,
      };

      try {
        const response = await addUpdateTimeOff(payload, authToken);
        if (!response.success) {
          setApiError(`${response.message}`);
        } else if (response) {
          setApiSuccessMessage("Time off request submitted successfully!");
          toast.success(response.message);
          setTimeout(() => {
            handleClose();
          }, 500);
        } else {
          setApiError("Received an unexpected response from the server.");
        }
      } catch (error: any) {
        setApiError(error.message || "An unexpected error occurred.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const fetchTimeOffs = async () => {
    if (currentUser) {
      try {
        const authToken = await currentUser.getIdToken();
        const userId = currentUser.uid;
        const result = await getUpcomingTimeOffs(userId, authToken);
        if (result.success) {
          setRemainingLeaveCount(result.data[0]?.remainingLeaveCount || 0);
        }
      } catch (error: any) {
        console.error("Error fetching time offs:", error);
      }
    }
  };

  const leaveTypeOptions = leaveTypes
    .filter((t: any) => t.isActive && !t.isDeleted)
    .map((t: any) => ({ value: t.id, label: t.requestType }));

  // Custom styles to make React-Select match Tailwind inputs
  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: "42px",
      borderRadius: "0.375rem", // rounded-md
      borderColor: state.isFocused ? "#7c3aed" : "#d1d5db", // purple-600 : gray-300
      boxShadow: state.isFocused ? "0 0 0 1px #7c3aed" : "none",
      "&:hover": {
        borderColor: "#7c3aed",
      },
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected ? "#7c3aed" : state.isFocused ? "#ede9fe" : "white", // purple-600 : purple-100
      color: state.isSelected ? "white" : "#1f2937", // text-gray-800
    }),
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      keyboard={false}
      backdrop="static"
      // Remove custom class, use Bootstrap/Tailwind utility logic via contentClassName if needed, 
      // or just rely on default Bootstrap clean styles.
    >
      {/* HEADER */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 bg-white rounded-t-lg">
        <h2 className="text-lg font-semibold text-gray-800 m-0">New Admin Request</h2>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 transition-colors bg-transparent border-0 p-1"
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </button>
      </div>

      {/* BODY */}
      <div className="p-4 bg-white">
        {apiError && (
          <Alert variant="danger" onClose={() => setApiError(null)} dismissible>
            {apiError}
          </Alert>
        )}
        {apiSuccessMessage && (
          <Alert variant="success" onClose={() => setApiSuccessMessage(null)} dismissible>
            {apiSuccessMessage}
          </Alert>
        )}

        {/* Request Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Request Type <span className="text-red-500">*</span>
          </label>
          <Select
            options={requestTypeOptions}
            value={requestTypeOptions.find((o) => o.value === selectedRequestType)}
            onChange={(option: any) => {
              setSelectedRequestType(option?.value || "");
              resetForm();
              setApiError(null);
            }}
            placeholder="Select"
            isSearchable={false}
            styles={selectStyles}
          />
        </div>

        {selectedRequestType && selectedRequestType !== "Time-Off Request" && (
          <Alert variant="info" className="text-sm">
            Submission for "{selectedRequestType}" is not yet supported.
          </Alert>
        )}

        {/* Leave Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type of Leave <span className="text-red-500">*</span>
          </label>
          <Select
            options={leaveTypeOptions}
            value={leaveTypeOptions.find((o) => o.value === formData.typeOfLeave) || null}
            onChange={(option: any) => {
              setFormData((prev) => ({
                ...prev,
                typeOfLeave: option ? option.value : 0,
              }));
            }}
            placeholder="Select Leave Type"
            isClearable
            isDisabled={selectedRequestType !== "Time-Off Request"}
            styles={selectStyles}
          />
        </div>

        {/* Leave Info Text */}
        {selectedRequestType === "Time-Off Request" && (
          <div className="mb-4 text-sm">
            {remainingLeaveCount >= 0 ? (
              <span className="text-green-600 font-medium">
                You have {remainingLeaveCount} remaining unit{remainingLeaveCount === 1 ? "" : "s"} of leave.
              </span>
            ) : (
              <span className="text-red-500 font-medium">
                You have exceeded your leave quota by {Math.abs(remainingLeaveCount)} day{Math.abs(remainingLeaveCount) === 1 ? "" : "s"}.
              </span>
            )}
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="timeOffStartTime"
              value={formData.timeOffStartTime}
              onChange={handleInputChange}
              min={new Date().toISOString().split("T")[0]}
              max={formData.timeOffEndTime || undefined}
              disabled={selectedRequestType !== "Time-Off Request"}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="timeOffEndTime"
              value={formData.timeOffEndTime}
              onChange={handleInputChange}
              min={formData.timeOffStartTime || new Date().toISOString().split("T")[0]}
              disabled={selectedRequestType !== "Time-Off Request"}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-400"
            />
          </div>
        </div>

        {/* Comment */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Comment (Optional)
          </label>
          <textarea
            rows={4}
            placeholder="Describe the work completed or progress made..."
            value={formData.comment}
            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
            disabled={selectedRequestType !== "Time-Off Request"}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-400 resize-none"
          />
        </div>
      </div>

      {/* FOOTER */}
      <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-4 py-3 bg-gray-50 rounded-b-lg">
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmitTimeOff}
          disabled={selectedRequestType !== "Time-Off Request" || !!apiSuccessMessage || isSubmitting}
          className="flex items-center justify-center rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Submitting...
            </>
          ) : (
            "Submit Request"
          )}
        </button>
      </div>
    </Modal>
  );
};

export default NewAdminRequestModal;